// ================================================================
// 交易記錄頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-14
// 功能：顯示使用者交易記錄（扣款、充值、贈送）
// ================================================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Avatar,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  AttachMoney as AttachMoneyIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import {
  getTransactionHistory,
  Transaction,
  formatTransactionType,
  formatDateTime
} from '../api/qa';
import { getDynamicPeriodLabel } from '../api/questions';
import { useAuth } from '../hooks/useAuth';
import { useCurrency } from '../contexts/CurrencyContext';

/**
 * 對交易記錄說明中儲存的靜態時間詞，套用月份/週次的動態標籤替換
 * 跳過年度替換，避免影響「今年xxx」固定類問題的顯示
 */
const applyPeriodToTransactionDesc = (text: string): string => {
  const now = new Date();
  const { label: monthLabel } = getDynamicPeriodLabel('monthly', now);
  const { label: weekLabel } = getDynamicPeriodLabel('weekly', now);
  return text
    .replace(/下個月|本月|未來一個月/g, monthLabel)
    .replace(/本週|下週|未來七天/g, weekLabel);
};

/**
 * 交易記錄頁面組件
 */
const TransactionHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await getTransactionHistory(50);

      if (result.success) {
        setTransactions(result.transactions);
      } else {
        setError('載入交易記錄失敗');
      }
    } catch (err: any) {
      setError(err.message || '載入交易記錄失敗');
    } finally {
      setLoading(false);
    }
  };

  // 取得交易類型顏色
  const getTransactionTypeColor = (type: string): 'success' | 'error' | 'info' | 'warning' => {
    if (type === 'register_bonus' || type === 'recharge') return 'success';
    if (type === 'query_charge') return 'error';
    if (type === 'refund') return 'info';
    return 'warning';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#667eea' }}>
        交易記錄
      </Typography>

      {/* 消費統計摘要 */}
      {user && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'info.main' }}>
                <AssessmentIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">累計消費</Typography>
                <Typography variant="h6" fontWeight="bold" color="info.dark">
                  {formatPrice(user.total_spent || 0)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main' }}>
                <AttachMoneyIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">目前餘額</Typography>
                <Typography variant="h6" fontWeight="bold" color={(user.balance ?? 0) < 0 ? 'error.main' : 'success.main'}>
                  {formatPrice(user.balance || 0)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'warning.main' }}>
                <AccessTimeIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">最後登入</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {user.last_login_at
                    ? new Date(user.last_login_at).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })
                    : 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  註冊：{user.created_at ? new Date(user.created_at).toLocaleDateString('zh-TW') : 'N/A'}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : transactions.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            尚無交易記錄
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>交易時間</strong></TableCell>
                <TableCell><strong>交易類型</strong></TableCell>
                <TableCell><strong>說明</strong></TableCell>
                <TableCell align="right"><strong>金額</strong></TableCell>
                <TableCell align="right"><strong>交易後餘額</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.transaction_id} hover>
                  <TableCell>{formatDateTime(transaction.created_at)}</TableCell>
                  <TableCell>
                    <Chip
                      label={formatTransactionType(transaction.transaction_type)}
                      size="small"
                      color={getTransactionTypeColor(transaction.transaction_type)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {applyPeriodToTransactionDesc(transaction.description)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                        color: transaction.amount >= 0 ? 'success.main' : 'error.main'
                      }}
                    >
                      {transaction.amount >= 0 ? '+' : ''}
                      {transaction.currency} {transaction.amount}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {transaction.currency} {transaction.balance_after}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default TransactionHistoryPage;
